// src/ConversationSidebar.jsx
// Sidebar panel for conversation history — paid users only

import { useState } from "react";

const ConversationSidebar = ({
  isOpen,
  onClose,
  conversations,
  activeConversationId,
  onSelectConversation,
  onNewConversation,
  onDeleteConversation,
  loading,
}) => {
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          onClick={onClose}
          style={{
            position: "fixed", inset: 0, zIndex: 199,
            backgroundColor: "rgba(0,0,0,0.3)",
            transition: "opacity 0.3s ease",
          }}
          aria-hidden="true"
        />
      )}

      {/* Sidebar panel */}
      <aside
        style={{
          position: "fixed",
          top: 0, left: 0, bottom: 0,
          width: "min(320px, 85vw)",
          zIndex: 200,
          backgroundColor: "var(--surface)",
          borderRight: "1px solid var(--border)",
          transform: isOpen ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          display: "flex", flexDirection: "column",
          boxShadow: isOpen ? "4px 0 24px rgba(0,0,0,0.08)" : "none",
        }}
        role="complementary"
        aria-label="Conversation history"
      >
        {/* Header */}
        <div style={{
          padding: "20px 16px 16px",
          borderBottom: "1px solid var(--border-light)",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <h2 style={{
            fontFamily: "'DM Serif Display', serif",
            fontSize: "18px", fontWeight: "400",
            color: "var(--ink)", letterSpacing: "-0.02em",
            margin: 0,
          }}>
            Your conversations
          </h2>
          <button
            onClick={onClose}
            aria-label="Close sidebar"
            style={{
              background: "none", border: "none", cursor: "pointer",
              fontSize: "20px", color: "var(--ink-muted)",
              padding: "4px 8px", lineHeight: 1,
            }}
          >×</button>
        </div>

        {/* New conversation button */}
        <div style={{ padding: "12px 16px 8px" }}>
          <button
            onClick={() => { onNewConversation(); onClose(); }}
            style={{
              width: "100%", padding: "10px 16px",
              fontSize: "14px", fontWeight: "500",
              backgroundColor: "var(--accent)", color: "#fff",
              border: "none", borderRadius: "8px",
              cursor: "pointer", fontFamily: "inherit",
              transition: "all 0.2s ease",
              display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = "var(--accent-light)"}
            onMouseLeave={(e) => e.target.style.backgroundColor = "var(--accent)"}
          >
            <span style={{ fontSize: "16px" }}>+</span> New conversation
          </button>
        </div>

        {/* Conversation list */}
        <div style={{
          flex: 1, overflowY: "auto",
          padding: "8px 12px 16px",
        }}>
          {loading ? (
            <p style={{
              fontSize: "14px", color: "var(--ink-muted)",
              textAlign: "center", padding: "24px 0",
            }}>Loading...</p>
          ) : conversations.length === 0 ? (
            <div style={{ textAlign: "center", padding: "32px 16px" }}>
              <p style={{
                fontSize: "14px", color: "var(--ink-muted)",
                lineHeight: 1.6,
              }}>
                No conversations yet. Start chatting with Irene and your conversations will appear here.
              </p>
            </div>
          ) : (
            conversations.map((convo) => (
              <div
                key={convo.id}
                style={{
                  marginBottom: "2px",
                  borderRadius: "8px",
                  backgroundColor: activeConversationId === convo.id
                    ? "var(--accent-mist)" : "transparent",
                  transition: "background-color 0.15s ease",
                }}
              >
                <button
                  onClick={() => { onSelectConversation(convo.id); onClose(); }}
                  style={{
                    width: "100%", padding: "10px 12px",
                    background: "none", border: "none", cursor: "pointer",
                    textAlign: "left", fontFamily: "inherit",
                    display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                    gap: "8px", borderRadius: "8px",
                  }}
                  onMouseEnter={(e) => {
                    if (activeConversationId !== convo.id) {
                      e.currentTarget.style.backgroundColor = "var(--surface-warm)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeConversationId !== convo.id) {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                  aria-current={activeConversationId === convo.id ? "true" : undefined}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      fontSize: "14px", fontWeight: activeConversationId === convo.id ? "600" : "400",
                      color: "var(--ink)",
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      marginBottom: "2px",
                    }}>
                      {convo.title || "New conversation"}
                    </p>
                    <p style={{
                      fontSize: "12px", color: "var(--ink-muted)",
                    }}>
                      {formatDate(convo.updated_at)}
                    </p>
                  </div>

                  {/* Delete button */}
                  {confirmDeleteId === convo.id ? (
                    <div style={{ display: "flex", gap: "4px", flexShrink: 0 }}
                      onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={(e) => { e.stopPropagation(); onDeleteConversation(convo.id); setConfirmDeleteId(null); }}
                        style={{
                          fontSize: "11px", padding: "2px 8px",
                          backgroundColor: "#c44", color: "#fff",
                          border: "none", borderRadius: "4px",
                          cursor: "pointer", fontFamily: "inherit",
                        }}
                      >Delete</button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}
                        style={{
                          fontSize: "11px", padding: "2px 8px",
                          backgroundColor: "var(--surface-mid)", color: "var(--ink-soft)",
                          border: "none", borderRadius: "4px",
                          cursor: "pointer", fontFamily: "inherit",
                        }}
                      >Cancel</button>
                    </div>
                  ) : (
                    <button
                      onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(convo.id); }}
                      aria-label={`Delete conversation: ${convo.title || "New conversation"}`}
                      style={{
                        background: "none", border: "none", cursor: "pointer",
                        fontSize: "14px", color: "var(--ink-faint)",
                        padding: "2px 6px", borderRadius: "4px",
                        opacity: 0.4, transition: "opacity 0.15s",
                        flexShrink: 0,
                      }}
                      onMouseEnter={(e) => e.target.style.opacity = "1"}
                      onMouseLeave={(e) => e.target.style.opacity = "0.4"}
                    >×</button>
                  )}
                </button>
              </div>
            ))
          )}
        </div>
      </aside>
    </>
  );
};

export default ConversationSidebar;
